import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalFooter,
  ModalHeader,
  Box,
  Button,
  ModalBody,
  Alert,
  useToast,
} from '@razorpay/blade/components';
import { FBPAllocationTable } from './FBPAllocationsAndDeclarations/AllocateDeclareFBPTable';
import { useAssignedComponents } from '../hooks/useAssignedComponents';
import { MetaInfo } from '../types';
import { useAllocateMutations } from '../hooks/useAllocateMutations';
import { FBPAllocateDeclareHeader } from './FBPAllocationsAndDeclarations/FBPAllocateDeclareHeader';
import { ConfirmationModal } from './FBPAllocationsAndDeclarations/ConfirmationModal';
import { EnterPRANModal } from './FBPAllocationsAndDeclarations/EnterPRANModal';
import { useMutation, useQueryClient } from 'react-query';
import { UpdateEmployeePranNumberRequest } from 'components/Settings/NPS/types';
import api from 'api';
import { parseError } from 'utils/Api';
import { AppError } from 'utils/AppError';
import { queryKeys } from '../constants';
import { usePayDetails } from 'components/PayDetails/context/PayDetailsContext';

interface AllocateDeclareFBPModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string | number | undefined;
  isAdmin: boolean;
  userName: string;
}

export const FBPAllocationDeclarationModal = ({
  isOpen,
  onClose,
  userId,
  isAdmin,
  userName = '',
}: AllocateDeclareFBPModalProps) => {
  const {
    flexibleComponentsData,
    isFetching: isFetchingAssignedComponents,
    isError: hasError,
    isLoading,
    monthlyDeclarations,
    yearlyDeclarationsInAmount,
    onMonthlyDeclarationChange,
    monthlyDeclarationSnapShot,
    selectedIdSnapShot,
    selectedIds,
    onSelectedIdsChange,
    invalidateQueries,
    monthlyDeclarationErrors,
    setMonthlyDeclarationErrors,
    totalAllocated,
    refetchData,
    isFlexibleDataReady,
  } = useAssignedComponents({ userId, shouldGetUnassignedComponents: isAdmin });

  const toast = useToast();
  const queryClient = useQueryClient();
  const {
    userData: { id: userOrgId, npsPran },
  } = usePayDetails();

  const {
    mutate: updateNpsSettings,
    isLoading: isUpdateNpsSettings,
    isError: isUpdateNpsSettingsError,
  } = useMutation({
    mutationFn: (settings: UpdateEmployeePranNumberRequest) => {
      return api.nps.updateEmployeePran(Number(userOrgId ?? '0'), settings);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.FETCH_USER_DETAILS);
      setShowEnterPRANModal(false);
      handleAllocate();
    },
    onError: (e: AppError) => {
      toast.show({ content: parseError(e), color: 'negative' });
    },
  });

  const handleSubmitPRAN = (pran: string) => {
    updateNpsSettings({
      pran_number: pran,
    });
  };
  const [selectedTiers, setSelectedTiers] = useState<Record<string, string>>({});

  useEffect(() => {
    const seeded: Record<string, string> = {};
    (flexibleComponentsData ?? []).forEach((row) => {
      if (row.selectedTierId) {
        seeded[row.id] = row.selectedTierId;
      }
    });
    setSelectedTiers((current) => ({ ...seeded, ...current }));
  }, [flexibleComponentsData]);

  const handleTierChange = useCallback(({ id, tierId }: { id: string; tierId: string }) => {
    setSelectedTiers((current) => ({ ...current, [id]: tierId }));
  }, []);

  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [showEnterPRANModal, setShowEnterPRANModal] = useState<boolean>(false);
  const meta: MetaInfo = {
    userId,
    getValue: (id: string) => monthlyDeclarations[id],
    getTierId: (id: string) => selectedTiers[id],
    now: Math.floor(Date.now() / 1000).toString(),
  };

  const closeConfirmationModal = () => setShowConfirmationModal(false);

  const {
    handleAllocateClick,
    isRunning,
    buildOps,
    checkForErrors,
    hasEmployerNPSComponentSelected,
  } = useAllocateMutations({
    selectedIds,
    monthlyDeclarations,
    previousSelectedIds: selectedIdSnapShot.current,
    previousMonthlyDeclarations: monthlyDeclarationSnapShot.current,
    meta,
    invalidateQueries,
    flexibleComponentsData,
    onSuccess: onClose,
    closeConfirmationModal,
    isAdmin,
  });
  const { createIds, removeIds, updatedAssignments } = buildOps();

  const isDisabled =
    (Object.keys(updatedAssignments).length === 0 &&
      (!isAdmin || (createIds.length === 0 && removeIds.length === 0))) ||
    isFetchingAssignedComponents;

  const handleAllocate = () => {
    if (isAdmin) {
      setShowConfirmationModal(true);
      return;
    }
    handleAllocateClick();
  };

  const handleSubmit: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    const errors = checkForErrors();
    if (Object.keys(errors).length) {
      setMonthlyDeclarationErrors(errors);
      return;
    }

    const checkForEmployerNPSComponent = hasEmployerNPSComponentSelected();
    if (checkForEmployerNPSComponent && !npsPran) {
      setShowEnterPRANModal(true);
      return;
    }
    handleAllocate();
  };

  const isFetching = isRunning;
  const showErrorAlert = (!isFetching || !isLoading) && hasError;
  const showLoader = isLoading || isFetchingAssignedComponents;

  return (
    <>
      <Modal isOpen={isOpen} onDismiss={onClose} size="full">
        <>
          <ModalHeader title="Allocate or declare FBP" subtitle="Learn more about FBP" />

          <ModalBody height="100%">
            {showErrorAlert ? (
              <Alert
                marginTop={'spacing.7'}
                description={
                  'We are facing some unknown error to fetch FBP data. Please try again.'
                }
                testID="allocation-modal-error-alert"
                color="negative"
                isFullWidth
                isDismissible={false}
                actions={{
                  primary: {
                    text: 'Retry',
                    onClick: refetchData,
                  },
                }}
              />
            ) : (
              <Box marginX="auto">
                <FBPAllocateDeclareHeader
                  isAdmin={isAdmin}
                  totalAllocated={totalAllocated}
                  yearlyDeclarationsInAmount={yearlyDeclarationsInAmount}
                />
                <FBPAllocationTable
                  key={isFlexibleDataReady ? 'ready' : 'loading'}
                  monthlyDeclaration={monthlyDeclarations}
                  flexibleData={flexibleComponentsData}
                  onChange={onMonthlyDeclarationChange}
                  setSelectedRows={onSelectedIdsChange}
                  isFetching={isFetching}
                  isLoading={showLoader}
                  selectedRows={selectedIds}
                  monthlyDeclarationErrors={monthlyDeclarationErrors}
                  isAdmin={isAdmin}
                  selectedTiers={selectedTiers}
                  onTierChange={handleTierChange}
                />
                {showConfirmationModal && (
                  <ConfirmationModal
                    isOpen={showConfirmationModal}
                    closeModal={closeConfirmationModal}
                    isDisabled={isRunning}
                    isLoading={isRunning}
                    primaryCTAAction={handleAllocateClick}
                    secondaryCTAAction={closeConfirmationModal}
                    userName={userName}
                  />
                )}
              </Box>
            )}
          </ModalBody>

          <ModalFooter>
            <Box display="flex" justifyContent="space-between" width="100%">
              <Button size="medium" variant="tertiary" onClick={onClose}>
                Close
              </Button>
              <Button
                size="medium"
                onClick={handleSubmit}
                isDisabled={isDisabled}
                isLoading={isRunning}>
                {isAdmin ? 'Allocate FBP' : 'Submit declaration'}
              </Button>
            </Box>
          </ModalFooter>
        </>

        {showEnterPRANModal && (
          <EnterPRANModal
            isOpen={showEnterPRANModal}
            onDismiss={() => setShowEnterPRANModal(false)}
            onConfirm={handleSubmitPRAN}
            isLoading={isUpdateNpsSettings}
            hasError={isUpdateNpsSettingsError}
          />
        )}
      </Modal>
    </>
  );
};
